import { ExpandMore } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Tooltip,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { fetchTestAutomationMetrics } from '../../../modules/dashboard';
import { RefreshButton } from '../../elements/CustomButtons';
import DetailsPageLink from '../../elements/DetailsPageLink';
import AccordionTable from '../../elements/common/AccordionTable';
import SmallSectionLoader from '../../elements/SmallSectionLoader';
import Error from '../../elements/Error';
import CommonDropdown from '../../elements/Dropdown';
import ExportButton from '../../elements/common/export/ExportButton';
import { exportData } from '../../elements/common/export/exportUtils';

const DISTRIBUTION_OPTIONS = [
  { key: 'country', label: 'Country-wise Distribution' },
  { key: 'module', label: 'Module-wise Distribution' },
];

const METRIC_COLUMNS = [
  'total_test_cases',
  'candidates_for_automation',
  'automated',
  'percentage_done',
  'yet_to_start',
  'in_progress_count',
  'blocked_count',
  'not_to_automate',
];

const baseTableHeaders = [
  {
    key: 'project_key',
    label: 'Key',
    sortable: true,
    formatContent: (row) => (
      <Tooltip title="Go to project" placement="right" arrow>
        <span>
          <DetailsPageLink
            to={`/project/${row.project_id}/testcases`}
            label={row.project_key}
          />
        </span>
      </Tooltip>
    ),
  },
  {
    key: 'project_name',
    label: 'Project Name',
    sortable: true,
    width: '30%',
    formatContent: (row) =>
      row.project_name?.slice(0, 25) +
      (row.project_name?.length > 25 ? '...' : ''),
  },
  { key: 'total_test_cases', label: 'Total Test Cases', sortable: true },
  { key: 'candidates_for_automation', label: 'Candidates for Automation', sortable: true },
  { key: 'automated', label: 'Automated', sortable: true },
  { key: 'percentage_done', label: 'Percentage Done (%)', sortable: true },
  { key: 'yet_to_start', label: 'Yet to Start', sortable: true },
  { key: 'in_progress_count', label: 'In Progress', sortable: true },
  { key: 'blocked_count', label: 'Blocked', sortable: true },
  { key: 'not_to_automate', label: 'Not to Automate', sortable: true },
];


const TestAutomationMetrics = ({ projectIds, showAccordion = true, page }) => {
  const [sortOrder, setSortOrder] = useState('asc');
  const [sortKey, setSortKey] = useState('project_key');
  const [distributionType, setDistributionType] = useState('country');
  const tableHeaders = useMemo(() => {
    if (page === 'dashboard') {
      return baseTableHeaders.filter(
        (col) => col.key !== 'project_key' && col.key !== 'project_name'
      );
    }
    return baseTableHeaders;
  }, [page]);
  const {
    data,
    isLoading: isAutomationLoading,
    isFetching: isAutomationFetching,
    isError,
    refetch: refetchAutomation,
  } = useQuery({
    queryKey: ['testAutomationMetrics', projectIds],
    queryFn: async () => {
      return fetchTestAutomationMetrics({ projectIds });
    },
    enabled: projectIds?.length > 0,
  });

  const handleSort = useCallback((key) => {
    if (sortKey === key) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortOrder('asc');
    }
  }, [sortKey]);

  const sortedData = useMemo(() => {
    const metrics = data?.data ?? [];
    if (!metrics.length) return [];

    return [...metrics].sort((a, b) => {
      const aVal = a?.[sortKey] ?? '';
      const bVal = b?.[sortKey] ?? '';
      const isNumeric = !isNaN(aVal) && !isNaN(bVal);
      const result = isNumeric ? Number(aVal) - Number(bVal) : String(aVal).localeCompare(String(bVal));
      return sortOrder === 'asc' ? result : -result;
    });
  }, [data?.data, sortKey, sortOrder]);

  const nestedTablesConfig = useMemo(() => {
    if (distributionType === 'country') {
      return [
        {
          dataKey: 'country_metrics',
          title: 'Country-wise Distribution',
          labelKey: 'country_combo',
          iconClass: 'bi bi-geo-alt-fill me-2 text-info',
          MainColName: 'Country Details',
        },
      ];
    }

    return [
      {
        dataKey: 'module_metrics',
        title: 'Module-wise Distribution',
        labelKey: 'module_name',
        iconClass: 'bi bi-box-seam me-2 text-info',
        MainColName: 'Module Details',
      },
    ];
  }, [distributionType]);

  if (isError) return (
    <Error>
      <p className="text-center">
        Unable to fetch data. Please contact your administrator to resolve this issue.
      </p>
    </Error>
  );

  if (isAutomationLoading) return <SmallSectionLoader />;
  const buildNestedRows = (projects, metricKey, nameKey, nameLabel) => {
    const columnLabels = Object.fromEntries(baseTableHeaders.map((h) => [h.key, h.label]));

    return projects.flatMap((project) => {
      const metrics = project[metricKey] || [];
      return metrics.map((metric, index) => ({
        [columnLabels.project_key]: index === 0 ? project.project_key : '',
        [columnLabels.project_name]: index === 0 ? project.project_name : '',
        [nameLabel]: metric[nameKey] ?? 'N/A',
        ...Object.fromEntries(
          METRIC_COLUMNS.map((key) => [columnLabels[key], metric[key] ?? 0])
        ),
      }));
    });
  };

  const buildProjectRows = (projects) => {
    return projects.map((project) =>
      Object.fromEntries(baseTableHeaders.map((col) => [col.label, project[col.key] ?? '']))
    );
  };

  const handleExport = (projects, type) => {
    const projectRows = buildProjectRows(projects);
    const countryRows = buildNestedRows(projects, 'country_metrics', 'country_combo', 'Country');
    const moduleRows = buildNestedRows(projects, 'module_metrics', 'module_name', 'Module');

    exportData({
      type,
      fileName: 'test_automation_metrics',
      sheets: [
        { name: 'Projects', data: projectRows },
        { name: 'Country-wise Distribution', data: countryRows },
        { name: 'Module-wise Distribution', data: moduleRows },
      ],
    });
  };

  const content = (
    <>
      <Box className="d-flex justify-content-between mb-3 align-items-center w-100">
        <CommonDropdown
          value={distributionType}
          options={DISTRIBUTION_OPTIONS}
          onChange={(e) => setDistributionType(e.target.value)}
          elementStyle={{ width: 240 }}
        />

        <div className="d-flex gap-2">
          <RefreshButton
            onRefresh={refetchAutomation}
            isFetching={isAutomationFetching}
            buttonSize={24}
            width={40}
          />
          <ExportButton
            tooltip="Export Test Automation Data"
            onExport={(type) => handleExport(sortedData, type)}
            disabled={!sortedData.length}
          />
        </div>
      </Box>

      <Row>
        <Col>
          <AccordionTable
            data={sortedData}
            headerTabs={tableHeaders}
            sortKey={sortKey}
            sortOrder={sortOrder}
            handleSort={handleSort}
            nestedTables={nestedTablesConfig}
            expandDefault={page === 'dashboard'}
          />
        </Col>
      </Row>
    </>
  );
  if (!showAccordion) {
    return (
      <div className="chart-div">
        <Typography variant="h5" className="dashboard-heading mt-2">
          Test Automation Coverage
        </Typography>
        {content}
      </div>
    );
  }

  return (
    <Accordion
      defaultExpanded
      sx={{
        borderRadius: '12px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
        marginBottom: '24px',
        backgroundColor: 'background.paper',
        '&:before': {
          display: 'none',
        },
      }}>
      <AccordionSummary
        expandIcon={<ExpandMore sx={{ color: 'primary.main' }} />}
        sx={{
          backgroundColor: 'action.hover',
          borderRadius: '12px 12px 0 0',
          minHeight: '64px',
          '&.Mui-expanded': {
            minHeight: '64px',
          },
          '& .MuiAccordionSummary-content': {
            margin: '12px 0',
            '&.Mui-expanded': {
              margin: '12px 0',
            },
          },
          '&:hover': {
            backgroundColor: 'action.selected',
          },
        }}>
        <Typography
          variant="h5"
          component="h2"
          sx={{ fontWeight: 600, color: 'text.primary' }}>
          Test Automation Coverage
        </Typography>
      </AccordionSummary>

      <AccordionDetails sx={{
        borderTop: '1px solid',
        borderColor: 'divider',
        padding: '24px',
        borderRadius: '0 0 12px 12px',
      }}>
        {content}
      </AccordionDetails>
    </Accordion>
  );
};

export default TestAutomationMetrics;
